import { Component, Injector, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormArray, Validators } from '@angular/forms';
import * as moment from 'moment';
import { HistoryData } from 'src/app/shared/components/history-view/history-view.component';
import { BuilderData } from 'src/app/shared/models/clincal-note.model';
import { LoadingStateService } from 'src/app/shared/services/common/loading-state.service';
import { validateAllFormFields } from 'src/app/shared/validators';
import { BuilderDataAbstract, AbstractBuilderComponent } from '../../shared/components/abstract-builder/abstract-builder.component';

type VitaminData = {
  _modified: number;
  date_time: Date | number;
  vitamin: string;
  site: string;
  repeat_status: string;
  next_date_time: Date | number | null;
}

interface VitaminFormData extends BuilderDataAbstract {
  records: Array<VitaminData>;
}

@Component({
  selector: 'app-injection-vitamins',
  templateUrl: './injection-vitamins.component.html',
  styleUrls: ['./injection-vitamins.component.scss']
})
export class InjectionVitaminsComponent extends AbstractBuilderComponent {

  @ViewChild('preview') preview!: TemplateRef<unknown>;
  public injectionList: string[] = [
    'Neurobion',
    'Vit B12',
    'Vit BCo',
    'Vit C',
  ].sort((a, b) => a.localeCompare(b));
  public siteList: string[] = [
    'Right Deltoid',
    'Left Deltoid',
    'Right Thigh',
    'Left Thigh',
    'Right Hip',
    'Left Hip',
    'Right Buttock',
    'Left Buttock',
  ].sort((a, b) => a.localeCompare(b));
  public repeatList: string[] = [
    'Once Off',
    'Repeat',
  ];

  constructor(
    private fb: FormBuilder
  ) {
    super();
    this.formData = this.fb.group({
      records: this.fb.array([])
    });
    this.addRow();
  }

  public getRow(): FormArray {
    return this.formData.get('records') as FormArray;
  }

  public removeRow(idx: number) {
    this.getRow().removeAt(idx);
    if (this.getRow().length === 0) {
      this.addRow();
    }
  }

  public addRow() {
    this.getRow().push(this.fb.group({
      date_time: [null, Validators.required],
      vitamin: [null, Validators.required],
      site: [null, Validators.required],
      repeat_status: [null, Validators.required],
      next_date_time: [null],
    }));
  }

  public override async validate() {
    validateAllFormFields(this.formData);

    return this.formData.valid;
  }

  serialize(data: VitaminFormData): HistoryData[] {
    if (!data) {
      return [];
    }

    return [{
      type: 'builder',
      builderTitle: 'INJECTABLE VITAMINS:',
      author: data.author ? data.author.name : '',
      dateTime: moment(data._modified!).format(),
      template: [this.preview, data]
    }];
  }

  asString(data: VitaminFormData): string {
    if (!data) return '';

    return [
      `[${moment(data._modified!).format('YYYY-MM-DD HH:mm')}${data.author ? `; ${data.author.name}` : ''}]: INJECTABLE VITAMINS`,
      data.records.map(
        (c, i) => `(${i + 1}): ${moment(c.date_time).format('YYYY-MM-dd')}  - ${c.vitamin} ${c.site} ${c.repeat_status}${c.next_date_time ? ` (${moment(c.next_date_time).format('YYYY-MM-dd')})` : ''}`
      ).join('; ')
    ].join('\n');
  }

  protected getBuilderData(): BuilderData {
    const data = this.formData.getRawValue() as VitaminFormData;
    data.records = data.records.map(d => ({ ...{ date_time: moment(d.date_time).valueOf() }, ...d }));
    let builderData = this.data;
    if (!builderData) {
      builderData = data;
    } else {
      builderData['records'] = data.records;
    }
    return builderData;
  }

  protected override setBuilderData(data: BuilderData): void {
    for (let i = 1; i < data['records'].length; i++) {
      this.addRow();
    }
    super.setBuilderData(data);
  }

}
